<template>
  <v-dialog :value="value" @input="emitInput" scrollable width="80%" style="z-index: 100;">
    <v-card style="max-height: 80vh; height: auto; overflow-y: auto; overflow-x: hidden;">
      <div class="upcoming-dialog-header">
        <v-card-title class="pa-0">
          <span class="headline">Proximas vacunas</span>
        </v-card-title>
        <div class="upcoming-dialog-actions">
          <v-btn
            small
            color="primary"
            title="Refrescar próximas vacunas"
            @click.prevent="loadVaccines"
          >
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
          <v-btn
            small
            color="error"
            title="Cerrar"
            @click.prevent="emitInput(false)"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </div>
      <v-card-text>
        <v-row class="pa-2 ml-2 text-uppercase">
          <v-col cols="4">
            <b>PACIENTE:</b> {{ patientName }} ({{ patientWeight }})
          </v-col>
          <v-col cols="3">
            {{ patientSpecies }} - {{ patientBreed }}
          </v-col>
        </v-row>
        <v-row class="pa-2 ml-2 text-uppercase">
          <v-col cols="12">
            <GeneralFilter
              entity="VacExp"
              :filterEndpoint="$API.veterinarymedication.getNextvacFiltered"
              :search="search"
              @emptyFilter="getVaccNextTime"
              @filtered="filterHandler"
            >
              <DxDataGrid
                :scrolling="{
                  mode: 'standard',
                  useNative: true,
                  showScrollbar: 'always'
                }"
                :show-borders="true"
                :data-source="vacData"
                key-expr="ID_VPMP"
                :column-auto-width="true"
                :word-wrap-enabled="true"
                :column-hiding-enabled="true"
                :on-row-prepared="onRowPrepared"
              >
                <DxColumn data-field="MedicationsDate" caption="Fecha"></DxColumn>
                <DxColumn data-field="Medicine" caption="Vacuna"></DxColumn>
                <DxColumn data-field="NextTime" caption="Proxima vez"></DxColumn>
                <DxColumn data-field="Weight" caption="Peso"></DxColumn>
                <DxColumn data-field="IsReminder" caption="Tipo" cell-template="recordType"></DxColumn>
                <DxColumn caption="No recordar" cell-template="notRemenberTemplate" width="120" />
                <template #recordType="{ data: cellData }">
                  {{ isReminderValue(cellData.data.IsReminder) ? 'Aplicación planificada' : 'Próxima aplicación' }}
                </template>
                <template #notRemenberTemplate="{ data: cellData }">
                  <v-tooltip bottom v-if="isPast(cellData.data?.FormattedDate)">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        color="error"
                        title="No recordar"
                        v-bind="attrs"
                        v-on="on"
                        @click="handleNotRemenber(cellData.data)"
                      >
                        <v-icon class="mr-1" color="white">mdi-bell-remove</v-icon>
                      </v-btn>
                    </template>
                    <span>No recordar</span>
                  </v-tooltip>
                </template>
              </DxDataGrid>
            </GeneralFilter>
          </v-col>
        </v-row>
      </v-card-text>
      <alerts
        v-if="alert.show"
        :properties="alert"
        v-on:close_alert="closeAlert"
        v-on:accept_alert="closeAlert"
      ></alerts>
    </v-card>
  </v-dialog>
</template>

<script>
import { DxDataGrid, DxColumn } from "devextreme-vue/data-grid";
import GeneralFilter from "@/components/GeneralFilter.vue";
import alerts from "@/components/Alerts";

export default {
  name: "UpcomingVaccinesModal",
  components: {
    DxDataGrid,
    DxColumn,
    GeneralFilter,
    alerts,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    patient: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      vacData: [],
      search: {
        ID_Patient: null,
      },
      alert: {
        type: "success",
        show: false,
        header: "",
        body: "",
      },
    };
  },
  computed: {
    patientId() {
      return this.patient?.ID_Patient || this.patient?.id_patient || null;
    },
    patientName() {
      return this.patient?.NamePatient || this.patient?.namepatient || "---";
    },
    patientSpecies() {
      return this.patient?.Species || this.patient?.species || "---";
    },
    patientBreed() {
      return this.patient?.Breed || this.patient?.breed || "---";
    },
    patientWeight() {
      const rawWeight = this.patient?.Weight || this.patient?.weight || "";
      const weightUnit = this.patient?.weightunit || "";
      if (!rawWeight && !weightUnit) {
        return "---";
      }
      if (rawWeight && weightUnit) {
        const normalizedWeight = String(rawWeight).trim();
        if (normalizedWeight.toLowerCase().includes(String(weightUnit).toLowerCase())) {
          return normalizedWeight;
        }
        return `${normalizedWeight} ${weightUnit}`.trim();
      }
      return String(rawWeight || weightUnit).trim();
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(isOpen) {
        if (isOpen) {
          this.loadVaccines();
        }
      },
    },
    patientId() {
      if (this.value) {
        this.loadVaccines();
      }
    },
  },
  methods: {
    emitInput(value) {
      this.$emit("input", value);
    },
    closeAlert() {
      this.alert.show = false;
    },
    showAlert(type, header, body) {
      this.alert.type = type || "danger";
      this.alert.header = header;
      this.alert.body = body;
      this.alert.show = true;
    },
    async loadVaccines() {
      if (!this.patientId) {
        this.vacData = [];
        this.search.ID_Patient = null;
        return;
      }

      this.search.ID_Patient = this.patientId;
      await this.getVaccNextTime();
    },
    isPast(nextTime) {
      if (!nextTime) return true;

      const nextDate = new Date(nextTime);
      const today = new Date();

      nextDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);

      return nextDate < today;
    },
    isReminderValue(value) {
      return value === true || Number(value) === 1;
    },
    async handleNotRemenber(data) {
      try {
        if (!data.ID_VPMP || !data.ID_Patient || !data.ID_Business) {
          throw new Error("Hay un problema en parametros. Recargue la pagina e intente de nuevo");
        }

        const result = await this.$API.veterinarymedication.setNotRemenber({
          ID_Patient: data.ID_Patient,
          ID_VPMP: data.ID_VPMP,
          ID_Business: data.ID_Business,
        });

        if (result) {
          await this.getVaccNextTime();
        }
      } catch (error) {
        console.log(error);
        this.showAlert(
          "warning",
          "Advertencia",
          "Ha ocurrido un error: " + (error?.message || "Intente de nuevo")
        );
      }
    },
    onRowPrepared(e) {
      if (e.rowType !== "data" || !e.data?.NextTime) {
        return;
      }

      const [dia, mes, anio] = e.data.NextTime.split("/");
      const fechaProxima = new Date(`${anio}-${mes}-${dia}T00:00:00`);
      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0);

      if (fechaProxima < hoy) {
        e.rowElement.style.color = "#a00";
      }
    },
    getVaccNextTime() {
      if (!this.patientId) {
        this.vacData = [];
        return Promise.resolve();
      }

      return this.$API.veterinarymedication
        .getNextvac({ ID_Patient: this.patientId })
        .then((res) => {
          this.vacData = res || [];
        })
        .catch((err) => {
          console.log(err);
          this.vacData = [];
        });
    },
    filterHandler(response) {
      this.vacData = response || [];
    },
  },
};
</script>

<style scoped>
.upcoming-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
}

.upcoming-dialog-actions {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}
</style>
